import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import { getApiUrl } from '../../lib/api';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Quiz question definitions ── */
interface Question {
  id: string;
  text: string;
  multi?: boolean;
  options: { value: string; label: string; description?: string }[];
}

const QUESTIONS: Question[] = [
  {
    id: 'effect',
    text: 'What effect are you looking for?',
    options: [
      { value: 'relaxation', label: 'Relaxation', description: 'Calm body and mind' },
      { value: 'energy', label: 'Energy', description: 'Uplifting and active' },
      { value: 'creativity', label: 'Creativity', description: 'Inspired and imaginative' },
      { value: 'pain_relief', label: 'Pain Relief', description: 'Soothe aches and tension' },
      { value: 'sleep', label: 'Sleep', description: 'Rest and recovery' },
      { value: 'social', label: 'Social / Euphoria', description: 'Giggly and talkative' },
    ],
  },
  {
    id: 'tolerance',
    text: "What's your tolerance level?",
    options: [
      { value: 'low', label: 'Low', description: 'New to cannabis' },
      { value: 'medium', label: 'Medium', description: 'Occasional user' },
      { value: 'high', label: 'High', description: 'Daily user' },
    ],
  },
  {
    id: 'flavor',
    text: 'Flavor preference?',
    options: [
      { value: 'earthy', label: 'Earthy / Woody' },
      { value: 'citrus', label: 'Citrus / Fruity' },
      { value: 'sweet', label: 'Sweet / Dessert' },
      { value: 'spicy', label: 'Spicy / Herbal' },
      { value: 'none', label: 'No preference' },
    ],
  },
  {
    id: 'medical',
    text: 'Any medical needs?',
    multi: true,
    options: [
      { value: 'none', label: 'None' },
      { value: 'anxiety', label: 'Anxiety' },
      { value: 'pain', label: 'Pain' },
      { value: 'insomnia', label: 'Insomnia' },
      { value: 'appetite', label: 'Appetite' },
      { value: 'depression', label: 'Depression' },
    ],
  },
  {
    id: 'time',
    text: 'Time of day?',
    options: [
      { value: 'morning', label: 'Morning' },
      { value: 'afternoon', label: 'Afternoon' },
      { value: 'evening', label: 'Evening' },
      { value: 'late_night', label: 'Late Night' },
    ],
  },
];

/* ── Strain type from API ── */
interface Strain {
  id?: number;
  name: string;
  type?: string;
  thc_content?: string;
  cbd_content?: string;
  effects?: string;
  flavors?: string;
  medical_uses?: string;
  description?: string;
  image_url?: string;
  rating?: number;
}

/* ── Answers record ── */
type Answers = Record<string, string | string[]>;

/* ── Scoring logic ── */
function scoreStrain(strain: Strain, answers: Answers): number {
  let score = 0;
  let maxScore = 0;

  const effectsLower = (Array.isArray(strain.effects) ? strain.effects.join(' ') : typeof strain.effects === 'string' ? strain.effects : '').toLowerCase();
  const flavorsLower = (Array.isArray(strain.flavors) ? strain.flavors.join(' ') : typeof strain.flavors === 'string' ? strain.flavors : '').toLowerCase();
  const medicalLower = (Array.isArray(strain.medical_uses) ? strain.medical_uses.join(' ') : typeof strain.medical_uses === 'string' ? strain.medical_uses : '').toLowerCase();
  const typeLower = (typeof strain.type === 'string' ? strain.type : '').toLowerCase();
  const descLower = (typeof strain.description === 'string' ? strain.description : '').toLowerCase();

  // Effect matching (weight: 30)
  maxScore += 30;
  const desiredEffect = answers.effect as string;
  const effectMap: Record<string, string[]> = {
    relaxation: ['relax', 'calm', 'mellow', 'peaceful', 'tranquil'],
    energy: ['energy', 'uplift', 'focus', 'alert', 'motivat'],
    creativity: ['creat', 'inspir', 'imaginat', 'cerebral', 'focus'],
    pain_relief: ['pain', 'relief', 'analgesic', 'sooth', 'numb'],
    sleep: ['sleep', 'sedat', 'drowsy', 'insomnia', 'rest'],
    social: ['euphori', 'happy', 'giggl', 'social', 'uplift', 'talk'],
  };
  const effectTerms = effectMap[desiredEffect] || [];
  const combined = effectsLower + ' ' + descLower;
  const effectHits = effectTerms.filter(t => combined.includes(t)).length;
  score += Math.min(30, (effectHits / Math.max(1, effectTerms.length)) * 30);

  // Tolerance → type matching (weight: 20)
  maxScore += 20;
  const tolerance = answers.tolerance as string;
  if (tolerance === 'low') {
    if (typeLower.includes('indica') || typeLower.includes('cbd')) score += 20;
    else if (typeLower.includes('hybrid')) score += 10;
  } else if (tolerance === 'medium') {
    if (typeLower.includes('hybrid')) score += 20;
    else score += 10;
  } else {
    if (typeLower.includes('sativa') || typeLower.includes('hybrid')) score += 20;
    else score += 10;
  }

  // Flavor matching (weight: 20)
  maxScore += 20;
  const flavorPref = answers.flavor as string;
  if (flavorPref !== 'none') {
    const flavorMap: Record<string, string[]> = {
      earthy: ['earth', 'wood', 'pine', 'herbal', 'musk'],
      citrus: ['citrus', 'lemon', 'orange', 'lime', 'fruit', 'berry', 'grape', 'mango'],
      sweet: ['sweet', 'dessert', 'cake', 'candy', 'vanilla', 'cookie', 'cream'],
      spicy: ['spic', 'pepper', 'clove', 'cinnamon', 'herb', 'diesel', 'pungent'],
    };
    const flavorTerms = flavorMap[flavorPref] || [];
    const flavorHits = flavorTerms.filter(t => flavorsLower.includes(t) || descLower.includes(t)).length;
    score += Math.min(20, (flavorHits / Math.max(1, flavorTerms.length)) * 20);
  } else {
    score += 15; // no pref = neutral bonus
  }

  // Medical matching (weight: 20)
  maxScore += 20;
  const medicalNeeds = answers.medical as string[];
  if (medicalNeeds.length === 0 || (medicalNeeds.length === 1 && medicalNeeds[0] === 'none')) {
    score += 15;
  } else {
    const medMap: Record<string, string[]> = {
      anxiety: ['anxiety', 'stress', 'calm', 'relax'],
      pain: ['pain', 'ache', 'inflammat', 'relief'],
      insomnia: ['insomnia', 'sleep', 'rest', 'sedat'],
      appetite: ['appetite', 'hunger', 'nausea', 'eat'],
      depression: ['depress', 'mood', 'uplift', 'happy'],
    };
    let medHits = 0;
    let medTotal = 0;
    for (const need of medicalNeeds) {
      if (need === 'none') continue;
      const terms = medMap[need] || [];
      medTotal += terms.length;
      medHits += terms.filter(t => medicalLower.includes(t) || effectsLower.includes(t) || descLower.includes(t)).length;
    }
    score += Math.min(20, (medHits / Math.max(1, medTotal)) * 20);
  }

  // Time of day matching (weight: 10)
  maxScore += 10;
  const time = answers.time as string;
  if (time === 'morning' || time === 'afternoon') {
    if (typeLower.includes('sativa')) score += 10;
    else if (typeLower.includes('hybrid')) score += 6;
    else score += 2;
  } else {
    if (typeLower.includes('indica')) score += 10;
    else if (typeLower.includes('hybrid')) score += 6;
    else score += 2;
  }

  return Math.round((score / maxScore) * 100);
}

/* ── Progress bar ── */
function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = ((current + 1) / total) * 100;
  return (
    <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
      <motion.div
        className="h-full bg-[#4ade80] rounded-full"
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      />
    </div>
  );
}

/* ── Main component ── */
export default function StrainQuizTab(_props: CountryProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [multiSelect, setMultiSelect] = useState<string[]>([]);
  const [results, setResults] = useState<{ strain: Strain; score: number }[] | null>(null);
  const [allStrains, setAllStrains] = useState<Strain[]>([]);
  const [loading, setLoading] = useState(false);
  const [direction, setDirection] = useState(1);

  const currentQ = QUESTIONS[step];
  const isLastStep = step === QUESTIONS.length - 1;

  // Pre-fetch strains
  useEffect(() => {
    fetch(getApiUrl('/api/strains-data?per_page=500'))
      .then(res => res.json())
      .then(result => {
        const items = Array.isArray(result) ? result : result.data ?? [];
        setAllStrains(items);
      })
      .catch(() => setAllStrains([]));
  }, []);

  const handleSelect = useCallback(
    (value: string) => {
      if (currentQ.multi) {
        setMultiSelect(prev => {
          if (value === 'none') return ['none'];
          const next = prev.filter(v => v !== 'none');
          return next.includes(value) ? next.filter(v => v !== value) : [...next, value];
        });
      } else {
        const newAnswers = { ...answers, [currentQ.id]: value };
        setAnswers(newAnswers);

        if (isLastStep) {
          computeResults(newAnswers);
        } else {
          setDirection(1);
          setTimeout(() => setStep(s => s + 1), 150);
        }
      }
    },
    [currentQ, answers, isLastStep],
  );

  const confirmMulti = useCallback(() => {
    const sel = multiSelect.length > 0 ? multiSelect : ['none'];
    const newAnswers = { ...answers, [currentQ.id]: sel };
    setAnswers(newAnswers);

    if (isLastStep) {
      computeResults(newAnswers);
    } else {
      setDirection(1);
      setStep(s => s + 1);
      setMultiSelect([]);
    }
  }, [multiSelect, answers, currentQ, isLastStep]);

  const goBack = useCallback(() => {
    if (step > 0) {
      setDirection(-1);
      setStep(s => s - 1);
      setMultiSelect([]);
    }
  }, [step]);

  const computeResults = (finalAnswers: Answers) => {
    setLoading(true);
    // Simulate a short delay for UX
    setTimeout(() => {
      const scored = allStrains
        .map(strain => ({ strain, score: scoreStrain(strain, finalAnswers) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
      setResults(scored);
      setLoading(false);
    }, 400);
  };

  const retake = () => {
    setStep(0);
    setAnswers({});
    setMultiSelect([]);
    setResults(null);
    setDirection(1);
  };

  const slideVariants = {
    enter: (d: number) => ({ x: d > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d > 0 ? -80 : 80, opacity: 0 }),
  };

  // ── Results view ──
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <div className="w-10 h-10 border-2 border-[#4ade80] border-t-transparent rounded-full animate-spin" />
        <p className="text-white/50 text-sm">Finding your perfect strains...</p>
      </div>
    );
  }

  if (results) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-xl font-bold text-white">Your Top Matches</h2>
          <p className="text-white/50 text-sm">Based on your preferences, these strains are the best fit for you.</p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {results.map(({ strain, score }, i) => (
            <motion.div
              key={strain.name + i}
              variants={staggerItem}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition-colors relative overflow-hidden"
            >
              {/* Match score badge */}
              <div className="absolute top-3 right-3">
                <span
                  className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                    score >= 70
                      ? 'bg-[#4ade80]/20 text-[#4ade80] border border-[#4ade80]/30'
                      : score >= 40
                        ? 'bg-[#d4a574]/20 text-[#d4a574] border border-[#d4a574]/30'
                        : 'bg-white/10 text-white/50 border border-white/10'
                  }`}
                >
                  {score}% match
                </span>
              </div>

              {strain.image_url && (
                <img
                  src={strain.image_url}
                  alt={strain.name}
                  className="w-full h-32 object-cover rounded-xl mb-3"
                />
              )}

              <div className="space-y-2">
                <div>
                  <h3 className="text-white font-semibold">{strain.name}</h3>
                  {strain.type && (
                    <span className="text-[10px] font-semibold uppercase text-[#d4a574]/80">{strain.type}</span>
                  )}
                </div>

                {strain.description && (
                  <p className="text-white/50 text-xs line-clamp-3 leading-relaxed">{strain.description}</p>
                )}

                {strain.effects && (
                  <div className="flex flex-wrap gap-1">
                    {strain.effects
                      .split(',')
                      .slice(0, 4)
                      .map((e, j) => (
                        <span key={j} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#4ade80]/10 text-[#4ade80]/80">
                          {e.trim()}
                        </span>
                      ))}
                  </div>
                )}

                {strain.flavors && (
                  <div className="flex flex-wrap gap-1">
                    {strain.flavors
                      .split(',')
                      .slice(0, 3)
                      .map((f, j) => (
                        <span key={j} className="px-1.5 py-0.5 text-[10px] rounded-full bg-[#d4a574]/10 text-[#d4a574]/80">
                          {f.trim()}
                        </span>
                      ))}
                  </div>
                )}

                <div className="flex gap-3 text-[10px] text-white/40">
                  {strain.thc_content && <span>THC: {strain.thc_content}</span>}
                  {strain.cbd_content && <span>CBD: {strain.cbd_content}</span>}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="flex justify-center">
          <button
            onClick={retake}
            className="px-6 py-2.5 text-sm font-semibold rounded-xl bg-[#4ade80]/20 border border-[#4ade80]/30 text-[#4ade80] hover:bg-[#4ade80]/30 transition-colors"
          >
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  // ── Quiz view ──
  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold text-white">Strain Finder Quiz</h2>
        <p className="text-white/40 text-sm">Answer {QUESTIONS.length} quick questions to find your ideal strain.</p>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-[10px] text-white/30">
          <span>Question {step + 1} of {QUESTIONS.length}</span>
          <span>{Math.round(((step + 1) / QUESTIONS.length) * 100)}%</span>
        </div>
        <ProgressBar current={step} total={QUESTIONS.length} />
      </div>

      {/* Question */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={step}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="space-y-4"
        >
          <h3 className="text-lg font-semibold text-white">{currentQ.text}</h3>
          {currentQ.multi && (
            <p className="text-white/30 text-xs">Select all that apply</p>
          )}

          <div className="grid gap-2">
            {currentQ.options.map(opt => {
              const isSelected = currentQ.multi
                ? multiSelect.includes(opt.value)
                : answers[currentQ.id] === opt.value;

              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  className={`text-left w-full px-4 py-3 rounded-xl border transition-all ${
                    isSelected
                      ? 'bg-[#4ade80]/10 border-[#4ade80]/40 text-[#4ade80]'
                      : 'bg-white/[0.02] border-white/10 text-white hover:border-white/20 hover:bg-white/[0.04]'
                  }`}
                >
                  <span className="text-sm font-medium">{opt.label}</span>
                  {opt.description && (
                    <span className="block text-xs mt-0.5 opacity-60">{opt.description}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Multi-select confirm */}
          {currentQ.multi && (
            <button
              onClick={confirmMulti}
              disabled={multiSelect.length === 0}
              className="w-full py-2.5 text-sm font-semibold rounded-xl bg-[#4ade80]/20 border border-[#4ade80]/30 text-[#4ade80] hover:bg-[#4ade80]/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Back button */}
      {step > 0 && (
        <button
          onClick={goBack}
          className="text-white/30 hover:text-white/60 text-xs transition-colors"
        >
          &larr; Previous question
        </button>
      )}
    </div>
  );
}
